import { EditIcon } from "@chakra-ui/icons";
import {
  Flex,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useContext, useState } from "react";

import { RanaModal } from "@nens/ranalib";

import { OrganisationContext } from "../../providers/OrganisationProvider";
import { getOrganisationWithUserRoles, useUserRoles } from "../../queries/general";

import { SaveSchematisationDataType } from "./SchematisationsDetail";

type ChangeSchemaOrganisationButtonProps = {
  saveSchematisationData: SaveSchematisationDataType;
  owner: string | undefined;
  disabled?: boolean;
};

function ChangeSchemaOrganisationButton({
  saveSchematisationData,
  owner,
  disabled = false,
}: ChangeSchemaOrganisationButtonProps) {
  const { organisations } = useContext(OrganisationContext);
  const organisationUserIsManagerOrCreator = getOrganisationWithUserRoles(["manager", "creator"]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const currentOrganisation = organisations.find((organisation) => organisation.uniqueId === owner);
  const [chosenOrgId, setChosenOrgId] = useState(currentOrganisation?.uniqueId);
  const userRoles = useUserRoles();
  const isManagerOrCreator = userRoles.some((role) => role === "manager" || role === "creator");

  return (
    <Flex alignItems="center">
      <InputGroup>
        <Input
          backgroundColor="brand.grey.100"
          borderWidth="base"
          borderStyle="solid"
          borderColor="brand.glacier"
          borderRadius="base"
          p={4}
          id="change-schema-organisation-input"
          value={currentOrganisation && currentOrganisation.name}
          readOnly
          _readOnly={{
            backgroundColor: "white",
          }}
        />
        <InputRightElement>
          <IconButton
            isDisabled={disabled || !isManagerOrCreator}
            icon={<EditIcon />}
            color="brand.analytic"
            onClick={onOpen}
            aria-label={"check"}
            backgroundColor="transparent"
            size="sm"
            alignSelf="center"
            title={
              isManagerOrCreator
                ? undefined
                : "You need Creator or Manager rights to transfer ownership of this schematisation to a different organisation."
            }
          ></IconButton>
        </InputRightElement>
      </InputGroup>
      <RanaModal
        isOpen={isOpen}
        onClose={onClose}
        topBar="Change schematisation organisation"
        buttons={[
          {
            variant: "secondary",
            children: "Cancel",
            onClick: onClose,
          },
          {
            isDisabled: currentOrganisation?.uniqueId === chosenOrgId,
            variant: "primary",
            children: "Transfer",
            onClick: () => {
              saveSchematisationData("owner", chosenOrgId);
              onClose();
            },
          },
        ]}
      >
        <FormLabel>
          To which organisation do you want to transfer the ownership of this schematisation?
        </FormLabel>
        <Select
          variant="outline"
          value={chosenOrgId}
          onChange={(event) => {
            const uniqueId = event.target.value;
            setChosenOrgId(uniqueId);
          }}
        >
          {organisationUserIsManagerOrCreator.map((org) => (
            <option key={org.uniqueId} value={org.uniqueId}>
              {org.name}
            </option>
          ))}
        </Select>
      </RanaModal>
    </Flex>
  );
}

export default ChangeSchemaOrganisationButton;
